import styled from 'styled-components'

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
`

export const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
`

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

export const Subtitle = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 15px;
  line-height: 20px;
`

export const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`

export const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[350]}, ${theme.shadows.control}`};
`

export const StatLabel = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 12px;
  line-height: 1;
  text-transform: uppercase;
`

export const StatValueRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
`

export const StatValue = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 26px;
  line-height: 32px;
  font-weight: 500;
`

export const StatSub = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 14px;
  line-height: 20px;
`

type Segment = { pct: number; color: string }

// Stacked bar: each segment is a share of the whole, drawn left to right.
export const StatBar = styled.div<{ $segments: Segment[] }>`
  height: 4px;
  overflow: hidden;
  border-radius: 2px;
  background: ${({ theme, $segments }) => {
    let start = 0
    const stops = $segments.map(({ pct, color }) => {
      const end = Math.min(100, start + Math.max(0, pct))
      const stop = `${color} ${start}% ${end}%`
      start = end
      return stop
    })
    return `linear-gradient(to right, ${[...stops, `${theme.grey[300]} ${start}% 100%`].join(', ')})`
  }};
`

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 20px 20px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[350]}, ${theme.shadows.control}`};

  @media (max-width: 768px) {
    padding: 12px;
  }
`

export const Count = styled.span`
  color: ${({ theme }) => theme.grey[700]};
  font-weight: 400;
`

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

export const Muted = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`

export const Primary = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 15px;
  line-height: 20px;
  overflow-wrap: anywhere;
`

export const StateLabel = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ $color }) => $color};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;

  &::before {
    content: '';
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

export const Tag = styled.span`
  max-width: 150px;
  overflow: hidden;
  padding: 3px 4px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.palette[200]};
  color: ${({ theme }) => theme.isDark ? theme.palette[900] : theme.grey[800]};
  font-size: 12px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OsCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: ${({ theme }) => theme.palette[900]};
  font-size: 15px;
  line-height: 20px;

  img {
    flex: none;
    width: 20px;
    height: 20px;
  }
`

export const OperationName = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 160px;
`

export const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: ${({ theme }) => theme.isDark ? theme.palette[900] : theme.grey[800]};
  font-size: 13px;
  line-height: 18px;
`

export const ProgressBar = styled.div<{ $pct: number | null }>`
  position: relative;
  height: 4px;
  overflow: hidden;
  border-radius: 2px;
  background: ${({ theme }) => theme.grey[350]};

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ $pct }) => $pct === null ? '30%' : `${$pct}%`};
    background: ${({ theme }) => theme.accent[100]};
    animation: ${({ $pct }) => $pct === null ? 'ds-indeterminate 1.4s ease-in-out infinite' : 'none'};
  }

  @keyframes ds-indeterminate {
    from { left: -30%; }
    to { left: 100%; }
  }

  @media (prefers-reduced-motion: reduce) {
    &::before { animation: none; }
  }
`

export const Spinner = styled.span`
  display: inline-block;
  flex: none;
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border: 2px solid ${({ theme }) => theme.accent[100]};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ds-spin 1s linear infinite;

  @keyframes ds-spin {
    to { transform: rotate(360deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const NameWithSpinner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 24px 0 4px;
  color: ${({ theme }) => theme.grey[600]};
  font-size: 15px;
  line-height: 20px;
`
